import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useBlock } from 'wagmi';

/**
 * Contracts judge time by the latest block, not by the user's device. The offset between the two is learned from
 * each new block, and every countdown and "is it unlocked yet" check uses chain time.
 *
 * A block's timestamp is rounded down to the second and reaches the page a moment after it was made, by however long
 * the network takes. Taken as is, every block nudged the clock back or forward by up to a second or two, so a
 * countdown showed some seconds twice and skipped others. So:
 * - the target offset is the largest sample seen (every sample is at or below the true offset: `nextOffset`);
 * - the clock moves towards it by at most 0.1 s per second (`slew`), never by a jump;
 * - each countdown ticks right after chain time crosses a whole second (`msToNextSecond`) and never goes backwards.
 */
export interface ChainClock {
  offset(): number; // seconds to add to Date.now()/1000
}

export function nextOffset(prev: number | null, sample: number): number {
  // a jump of more than 30 s means the device clock was changed (or the chain stalled and resumed): start again
  if (prev === null || Math.abs(sample - prev) > 30) return sample;
  return Math.max(prev, sample);
}

/** The shown offset `elapsed` seconds later, moving towards `target` at no more than `rate` seconds per second. */
export function slew(shown: number, target: number, elapsed: number, rate = 0.1): number {
  if (Math.abs(target - shown) > 30) return target;
  const step = rate * Math.max(0, elapsed);
  return target > shown ? Math.min(target, shown + step) : Math.max(target, shown - step);
}

/** Milliseconds until chain time next crosses a whole second, so a countdown ticks exactly once per second. */
export function msToNextSecond(nowMs: number, offsetSec: number): number {
  const chainMs = nowMs + offsetSec * 1000;
  return 1000 - (((chainMs % 1000) + 1000) % 1000) + 5;
}

function makeClock() {
  let target: number | null = null;
  let shown = 0;
  let at = Date.now() / 1000;
  return {
    sample(s: number) {
      const first = target === null;
      target = nextOffset(target, s);
      if (first || Math.abs(target - shown) > 30) {
        shown = target;
        at = Date.now() / 1000;
      }
    },
    offset() {
      const now = Date.now() / 1000;
      if (target !== null) shown = slew(shown, target, now - at);
      at = now;
      return shown;
    },
  };
}

const Ctx = createContext<ChainClock>({ offset: () => 0 });

export function ChainClockProvider({ children }: { children: ReactNode }) {
  const block = useBlock({ watch: true, query: { refetchInterval: 10_000 } });
  const [clock] = useState(makeClock);
  useEffect(() => {
    if (block.data?.timestamp !== undefined) clock.sample(Number(block.data.timestamp) - Date.now() / 1000);
  }, [block.data?.timestamp, clock]);
  return <Ctx.Provider value={clock}>{children}</Ctx.Provider>;
}

/** Current chain time in seconds, re-rendering every `tickMs` (every second: on the second). Never goes backwards. */
export function useChainNow(tickMs = 1000): number {
  const clock = useContext(Ctx);
  const [, setTick] = useState(0);
  const last = useRef(0);
  useEffect(() => {
    let id: ReturnType<typeof setTimeout>;
    const wait = () => (tickMs === 1000 ? msToNextSecond(Date.now(), clock.offset()) : tickMs);
    const tick = () => {
      setTick((n) => n + 1);
      id = setTimeout(tick, wait());
    };
    id = setTimeout(tick, wait());
    return () => clearTimeout(id);
  }, [tickMs, clock]);
  const t = Math.max(last.current, Date.now() / 1000 + clock.offset());
  last.current = t;
  return t;
}
